import React from "react";
import { Button, CircularProgress, Box, useTheme } from "@mui/material";
import { tokens } from "../../theme";

/**
 * AppButton - A standardized reusable button.
 *
 * @param {boolean} pill - If true, makes the button fully rounded (pill shape)
 * @param {boolean} isLoading - Shows spinner and disables button
 */
const AppButton = ({
  children,
  onClick,
  variant = "contained",
  color = "primary",
  size = "medium",
  startIcon,
  endIcon,
  disabled = false,
  isLoading = false,
  loadingText = "Please wait...",
  fullWidth = false,
  pill = false, // New prop for POS style
  type = "button",
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const appTheme = tokens(theme.palette.mode);

  // --- GRADIENT LOGIC ---
  const isGradient = variant === "gradient";
  const muiVariant = isGradient ? "contained" : variant;

  const gradientStyle = isGradient
    ? {
        background: appTheme.gradients[color] || appTheme.gradients.info,
        color: "#ffffff",
        border: "none",
        boxShadow: theme.shadows[3],
        "&:hover": {
          boxShadow: theme.shadows[6],
          transform: "translateY(-1px)",
          filter: "brightness(1.1)",
        },
        "&:active": {
          transform: "translateY(0px)",
          boxShadow: theme.shadows[2],
        },
        "&.Mui-disabled": {
          background: appTheme.gradients[color] || appTheme.gradients.info,
          opacity: 0.6,
          color: "#ffffff",
        },
      }
    : {};

  return (
    <Button
      type={type}
      onClick={onClick}
      variant={muiVariant}
      color={color}
      size={size}
      disabled={disabled || isLoading}
      fullWidth={fullWidth}
      startIcon={!isLoading ? startIcon : null}
      endIcon={!isLoading ? endIcon : null}
      disableElevation={isGradient}
      sx={{
        borderRadius: pill ? "50px" : theme.appRadius.sm, // Toggles shape!
        fontWeight: 600,
        textTransform: "none",
        minHeight:
          size === "large"
            ? 48
            : size === "small"
              ? 32
              : theme.appSizes.buttonHeightMedium,
        px: variant === "text" ? 1.5 : 3,
        transition: "all 0.2s ease-in-out",

        whiteSpace: "nowrap", // Forces one line
        overflow: "hidden", // Hides overflow
        textOverflow: "ellipsis", // Adds "..." if squished
        display: "flex", // Ensures icon and text align
        "& .MuiButton-startIcon": {
          flexShrink: 0, // Prevents the icon from getting crushed
        },
        "& .MuiButton-endIcon": {
          flexShrink: 0,
        },

        ...gradientStyle,

        ...(variant === "outlined" && {
          borderWidth: "1.5px",
          "&:hover": { borderWidth: "1.5px" },
        }),
        ...(variant === "text" && {
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.05)", // Subtle hover for dark mode text buttons
          },
        }),

        ...sx,
      }}
      {...props}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={20} color="inherit" thickness={4} />
          {loadingText && <span>{loadingText}</span>}
        </Box>
      ) : (
        children
      )}
    </Button>
  );
};

export default AppButton;
